import { message } from 'antd'
import QRCode from 'qrcode'

export interface IPrintHTMLOptions {
  qrCodeCanvasId?: string
  qrCodeUrl?: string
}

// The section is often inside a modal that mounts its content a frame or more after it opens
const PRINT_SECTION_POLL_MS = 50
const PRINT_SECTION_TIMEOUT_MS = 3000
// Fallback cleanup for browsers that never fire `afterprint` on the frame
const PRINT_FRAME_CLEANUP_MS = 60000

// Receipts generated by @getgreenline/receipts all share the same id, so several can be mounted at
// once. The last top-level match is the most recently rendered one (modals mount at the end of body).
const findPrintSection = (id: string): HTMLElement | undefined => {
  const matches = Array.from(document.querySelectorAll<HTMLElement>(`[id="${id}"]`))
  const topLevelMatches = matches.filter(
    (element) => !matches.some((other) => other !== element && other.contains(element)),
  )
  return topLevelMatches[topLevelMatches.length - 1]
}

const createPrintFrame = () => {
  const iframe = document.createElement('iframe')
  iframe.id = `print-frame-${new Date().getTime()}`
  iframe.src = 'about:blank'
  // Not display:none, some browsers skip laying out (and printing) hidden frames
  iframe.style.position = 'absolute'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)
  return iframe
}

// innerHTML copies <canvas> tags but not their pixels (QR codes, barcodes)
const copyCanvases = (source: HTMLElement, target: HTMLElement) => {
  const sourceCanvases = source.querySelectorAll('canvas')
  const targetCanvases = target.querySelectorAll('canvas')

  sourceCanvases.forEach((sourceCanvas, index) => {
    const targetCanvas = targetCanvases[index]
    // drawImage throws on an empty canvas, e.g. a QR code that was never drawn
    if (!targetCanvas || !sourceCanvas.width || !sourceCanvas.height) return

    targetCanvas.width = sourceCanvas.width
    targetCanvas.height = sourceCanvas.height
    targetCanvas.getContext('2d')?.drawImage(sourceCanvas, 0, 0)
  })
}

const printSection = (section: HTMLElement, title?: string, options?: IPrintHTMLOptions) => {
  const iframe = createPrintFrame()
  const removeFrame = () => iframe.remove()

  try {
    const printWindow = iframe.contentWindow
    if (!printWindow) throw new Error('Print frame has no window')

    const printDocument = printWindow.document
    const printCSS =
      (document.getElementById('printing-css') as HTMLTextAreaElement | null)?.value ?? ''

    printDocument.title = title || document.title
    printDocument.body.innerHTML = '<style>' + printCSS + '</style>' + section.innerHTML
    copyCanvases(section, printDocument.body)

    if (options?.qrCodeCanvasId && options?.qrCodeUrl) {
      const blankCanvas = printDocument.getElementById(options.qrCodeCanvasId)
      if (blankCanvas) QRCode.toCanvas(blankCanvas, options.qrCodeUrl).catch(console.error)
    }

    // print() returns immediately on Safari/iPadOS, so the frame must outlive the call
    printWindow.addEventListener('afterprint', removeFrame)
    setTimeout(removeFrame, PRINT_FRAME_CLEANUP_MS)

    printWindow.focus()
    printWindow.print()
  } catch (error) {
    console.error('[printHTML ERROR]', error)
    message.error('There was an error printing. Please try again.')
    removeFrame()
  }
}

export function printHTML(id: string, title?: string, options?: IPrintHTMLOptions) {
  const section = findPrintSection(id)
  // Print in the same tick when possible, so the call stays inside the user's click
  if (section) return printSection(section, title, options)

  const startedAt = Date.now()
  const waitForSection = () => {
    const mountedSection = findPrintSection(id)
    if (mountedSection) return printSection(mountedSection, title, options)

    if (Date.now() - startedAt < PRINT_SECTION_TIMEOUT_MS) {
      setTimeout(waitForSection, PRINT_SECTION_POLL_MS)
      return
    }

    console.error(`[printHTML ERROR] #${id} was not rendered`)
    message.error('There was nothing to print. Please try again.')
  }
  setTimeout(waitForSection, PRINT_SECTION_POLL_MS)
}
